import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import TruncatedText from "../../components/common/TruncatedText";
import Actions from "../../components/common/Actions";
import CustomDateTimePicker from "../../components/common/CustomDateTimePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import {
  addCostPlanApi,
  CostPlanBulkApi,
  CostPlanHistory,
  CostPlanType,
  deleteCostPlanApi,
  editCostPlanApi,
  exportCostPlan,
  fetchCostPlanHistory,
  useCostPlanQuery,
  useNoDataQuery,
  CostPlanCostComparison,
} from "../../services/CostPlanService";
import { fetchCountry } from "../../services/CountryService";
import { fetchCurrency } from "../../services/CurrencyService";
import { fetchOperator } from "../../services/OperatorsService";
import { fetchProfile } from "../../services/ProfilesService";
import {
  fetchProvider,
  fetchProviders,
} from "../../services/ProviderService";
import {
  ErrorResponse,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
} from "../../utils/commonDataUtils";
import History from "../../components/History";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const COST_COMPARISON_OPTIONS: Option[] = [
  { label: "Greater than >", value: "GT" },
  { label: "Less than <", value: "LT" },
  { label: "Equal =", value: "EQ" },
];

/** Allows typing decimals like 0.05 without coercing away trailing zeros mid-edit */
function isCostInputValidForSearch(raw: string): boolean {
  const t = raw.trim();
  if (t === "" || t === "." || t === "-" || t === "-.") return false;
  if (t.endsWith(".")) return false;
  const n = Number.parseFloat(t);
  return Number.isFinite(n);
}

const CostPlanPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<CostPlanType & { countryId: number }>
  >({});
  const [appliedFilters, setAppliedFilters] = useState<Params<CostPlanType>>(
    {},
  );
  const [showTable, setShowTable] = useState(false);
  const [costValueInput, setCostValueInput] = useState("");

  const dialogRef = useRef<CostPlanDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "delete" | "history" | "edit",
    id?: string,
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: CostPlanType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      console.log("Selected row IDs:", newSelected);
      return newSelected;
    });
  };

  const isRowSelected = (row: CostPlanType) =>
    selectedRowIds.includes(String(row.id));

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countriesAsOptions = await loadCountriesOptions();
        setCountriesOptions(countriesAsOptions);

        const providerResponse = await fetchProviders({
          size: config.MAX_PAGE_SIZE,
        });
        const providersAsOptions = providerResponse.content.map((provider) => ({
          label: provider.name,
          value: provider.id,
        }));
        setProvidersOptions(providersAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    const next: Params<CostPlanType & { countryId: number }> = { ...filters };
    if (!isCostInputValidForSearch(costValueInput)) {
      delete next.costValue;
      delete next.costComparison;
    } else {
      next.costValue = Number.parseFloat(costValueInput.trim());
      next.costComparison = next.costComparison ?? "EQ";
    }
    setAppliedFilters(next);
    setShowTable(true);
    setSelectedRowIds([]);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ROUTING_COST"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_ROUTING_COSTS_HISTORY"),
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Provider",
      value: filters.vendorConnectionProfileId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          vendorConnectionProfileId: String(selectedProvider?.value ?? ""),
        }));

        console.log("Selected providerId:", selectedProvider?.label ?? "");
      },
    },
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      clearable: true,
      placeholder: "Select Country",
      onChange: async (value: string | number | (string | number)[]) => {
        let selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setIsOperatorDisabled(true);

        if (value === "" || value === null || value === undefined) {
          setFilters((prev) => ({
            ...prev,
            countryId: undefined,
            operatorId: undefined,
          }));
          setOperatorsByCountry([]);
          return;
        }

        try {
          const countryId = Number(selectedCountry?.value);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
          setFilters((prev) => ({
            ...prev,
            countryId: countryId,
            operatorId: undefined,
          }));

          console.log("Selected country:", selectedCountry?.label ?? "");
        } catch (error: any) {
          console.error("Error in country change:", error);
          setOperatorsByCountry([]);
          ToastNotification.error(
            "Error loading operators for selected country",
          );
        } finally {
          setIsOperatorDisabled(false);
        }
      },
    },

    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      clearable: true,
      disabled: isOperatorDisabled,

      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          operatorId:
            value === undefined || value === null || value === ""
              ? undefined
              : Number(selectedOperator?.value),
        }));
        console.log("Selected operator:", selectedOperator?.label ?? "");
      },
    },
    {
      label: "Cost comparison",
      value: filters.costComparison ?? "",
      options: COST_COMPARISON_OPTIONS,
      clearable: true,
      placeholder: "Select comparison",
      onChange: (value: string | number | (string | number)[]) => {
        if (value === "" || value === null || value === undefined) {
          setFilters((prev) => ({ ...prev, costComparison: undefined }));
          return;
        }
        const v = Array.isArray(value) ? value[0] : value;
        setFilters((prev) => ({
          ...prev,
          costComparison: String(v) as CostPlanCostComparison,
        }));
      },
    },
  ];

  const costFilterTextFields = [
    {
      label: "Cost value",
      type: "text" as const,
      inputMode: "decimal" as const,
      value: costValueInput,
      placeholder: "e.g. 0.05",
      helperText: "",
      error: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (raw === "") {
          setCostValueInput("");
          setFilters((prev) => {
            const next = { ...prev };
            delete next.costValue;
            delete next.costComparison;
            return next;
          });
          return;
        }
        if (!/^-?\d*\.?\d*$/.test(raw)) return;
        setCostValueInput(raw);
        setFilters((prev) => ({
          ...prev,
          costComparison: prev.costComparison ?? "EQ",
        }));
      },
      borderRadius: "8px",
    },
  ];

  const isSearchDisabled = !(
    (filters.vendorConnectionProfileId &&
      String(filters.vendorConnectionProfileId).trim() !== "") ||
    filters.countryId !== undefined ||
    (filters.operatorId !== undefined && filters.operatorId !== null) ||
    isCostInputValidForSearch(costValueInput)
  );

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      disabled: isSearchDisabled,
      permission: "GET_ALL_ROUTING_COSTS",
    },
  ];

  const fields = useMemo<IField<CostPlanType>[]>(() => [
    {
      key: "vendorConnectionProfileId",
      header: "Provider",
      transform: async (t) => {
        if (!t.vendorConnectionProfileId) return "-";
        try {
          const provider = await queryClient.fetchQuery({
            queryKey: ["provider", t.vendorConnectionProfileId],
            queryFn: () => fetchProvider(t.vendorConnectionProfileId),
          });
          return provider?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "Country",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          if (!operator?.countryId) return "-";
          const country = await queryClient.fetchQuery({
            queryKey: ["country", operator.countryId],
            queryFn: () => fetchCountry(operator.countryId),
          });
          return country?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "Operator",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return operator?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "CC",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return operator?.cc ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "MCC",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return operator?.mcc?.join(",") ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "operatorId",
      header: "MNC",
      transform: async (t) => {
        if (!t.operatorId) return "-";
        try {
          const operator = await queryClient.fetchQuery({
            queryKey: ["operator", t.operatorId],
            queryFn: () => fetchOperator(t.operatorId),
          });
          return <TruncatedText text={operator?.mnc?.join(",")} />;
        } catch {
          return "-";
        }
      },
    },
    { key: "cost", header: "Cost" },
    {
      key: "vendorConnectionProfileId",
      header: "Currency",
      transform: async (t) => {
        if (!t.vendorConnectionProfileId) return "-";
        try {
          const provider = await queryClient.fetchQuery({
            queryKey: ["provider", t.vendorConnectionProfileId],
            queryFn: () => fetchProvider(t.vendorConnectionProfileId),
          });
          const profile = await queryClient.fetchQuery({
            queryKey: ["profile", provider.profileId],
            queryFn: () => fetchProfile(provider.profileId),
          });
          const currency = await queryClient.fetchQuery({
            queryKey: ["currency", profile.currencyId],
            queryFn: () => fetchCurrency(profile.currencyId),
          });
          return currency?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    { key: "newCost", header: "New Cost" },
    { key: "effectiveDate", header: "Effective Date" },
  ], [queryClient]);

  const handleCountryChange = async (
    value: string | number | (string | number)[],
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;

    if (selectedCountry) {
      try {
        const countryId = Number(selectedCountry);
        if (isNaN(countryId)) {
          throw new Error("Invalid country ID");
        }

        setIsOperatorDisabled(true);
        const operators = await loadOperatorsByCountry(countryId);
        setOperatorsByCountry(operators);
      } catch (error: any) {
        console.error("Error fetching operators for selected country:", error);
        setOperatorsByCountry([]);
      } finally {
        setIsOperatorDisabled(false);
      }
    } else {
      setOperatorsByCountry([]);
      setIsOperatorDisabled(false);
    }
  };

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Cost Plan"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          autoCompletes={autoCompletes}
          textFields={costFilterTextFields}
          buttons={buttons}
        />
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}></Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Cost Plan List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={filters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_ROUTING_COST",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_ROUTING_COST",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportCostPlan(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_ROUTING_COSTS_CSV",
            },
          ]}
        />

        <CustomTable<CostPlanType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={showTable ? useCostPlanQuery : useNoDataQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["cost", "effectiveDate"]}
        />
        {!showTable && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="120px"
          >
            <CustomLabel
              text="Please use the search above to display cost plan data."
              variant="body1"
              fontWeight={400}
              color={COLORS.placeHolderColor}
              marginTop={2}
              marginBottom={2}
            />
          </Box>
        )}
      </Box>
      <CostPlanDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        providersOptions={providersOptions}
        countriesOptions={countriesOptions}
        appliedFilters={appliedFilters}
        loadOperatorsByCountry={loadOperatorsByCountry}
      />
    </Box>
  );
};

interface DialogData extends Omit<CostPlanType, "operatorId"> {
  countryId: number;
  operatorId?: number | number[];
  currencyName: string;
  effectiveDate: Date;
  name: string;
}
export interface CostPlanDialogRef {
  openDialog: (
    mode: "add" | "delete" | "history" | "edit",
    id?: string,
  ) => void;
}

interface CostPlanDialogProps {
  selectedRowIds: string[];
  onRefresh: () => void;
  onClearSelection: () => void;
  countriesOptions: Option[];
  appliedFilters: Params<CostPlanType>;

  providersOptions: Option[];
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const CostPlanDialog = forwardRef<CostPlanDialogRef, CostPlanDialogProps>(
  (
    {
      onRefresh,
      countriesOptions,
      loadOperatorsByCountry,
      providersOptions,

      appliedFilters,
      selectedRowIds,
      onClearSelection,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "delete" | "history" | "edit"
    >("add");
    const [dialogData, setDialogData] = useState<Partial<DialogData>>({});

    const [selectedId, setSelectedId] = useState<string | undefined>();

    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        if (mode === "history") {
          setSelectedId(id);
          setDialogOpen(true);
        }
        setDialogMode(mode);
        setSelectedId(id);
        openDialog(mode, id);
      },
    }));

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country.",
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };
    useEffect(() => {
      const isAnyFieldEmpty =
        !dialogData.cost ||
        !dialogData.vendorConnectionProfileId ||
        !dialogData.operatorId;
      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const openDialog = async (
      mode: "add" | "delete" | "edit" | "history",
      id?: string,
    ) => {
      setDialogMode(mode);

      try {
        if (mode === "add") {
          setDialogData({
            vendorConnectionProfileId: "",
            operatorId: [],
            cost: undefined,
            comment: "",
            currencyName: "",
          });
          setDialogOpen(true);
        } else if (mode === "edit" && id) {
          const costPlanData = await editCostPlanApi(id);
          setDialogData({
            ...costPlanData,
            operatorId: costPlanData.operatorId
              ? [costPlanData.operatorId]
              : [],
          });

          if (costPlanData.operatorId) {
            const { countryId: selectedCountry } = await fetchOperator(
              costPlanData.operatorId,
            );

            if (selectedCountry) {
              const countryId = Number(selectedCountry);
              setIsOperatorDisabled(true);
              const ops = await loadOperatorsByCountry(countryId);
              setOperatorsByCountry(ops);
              setDialogData((prev) => ({
                ...prev,
                countryId: countryId,
                operatorId: Array.isArray(costPlanData.operatorId)
                  ? costPlanData.operatorId
                  : [costPlanData.operatorId],
              }));
              setIsOperatorDisabled(false);
            }
          }

          if (costPlanData.vendorConnectionProfileId) {
            try {
              const provider = await fetchProvider(
                costPlanData.vendorConnectionProfileId,
              );
              const profile = await fetchProfile(provider.profileId);
              const currency = await fetchCurrency(profile.currencyId);
              setDialogData((prev) => ({
                ...prev,
                currencyName: currency.name,
              }));
            } catch (error: any) {
              console.error(
                "Error fetching currency for selected provider:",
                error,
              );
              ToastNotification.error(error.message);
              setDialogData((prev) => ({ ...prev, currencyName: "" }));
            }
          }

          setDialogOpen(true);
        } else if (mode === "delete") {
          setDialogOpen(true);
        }
      } catch (error: any) {
        console.error(`Error fetching cost data for ${mode} mode:`, error);
      }
    };

    const closeDialog = () => {
      setDialogOpen(false);
      setIsOperatorDisabled(true);
      setDialogData({});
    };

    const addCostPlan = async (data: Partial<DialogData>) => {
      try {
        const { operatorId, ...restData } = data;
        const commonData: Partial<CostPlanType> = {
          ...restData,
          isDeleted: false,
        };

        if (Array.isArray(operatorId) && operatorId.length > 1) {
          const payloads: Partial<CostPlanType>[] = operatorId.map(
            (id, index) => ({
              ...commonData,
              operatorId: id,
              name: data.name ? `${data.name}_${index + 1}` : data.name,
            }),
          );
          await CostPlanBulkApi(payloads);
        } else {
          const payload: Partial<CostPlanType> = {
            ...commonData,
            operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
          };
          await addCostPlanApi(payload);
        }

        onRefresh();
        closeDialog();
        setDialogData({ effectiveDate: new Date() });
        ToastNotification.success("Cost added successfully!");
      } catch (error: any) {
        console.error("Error adding cost plan:", error);
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError.response?.data?.message ??
          typedError.message ??
          "An unknown error occurred while adding the cost plan.";
        ToastNotification.error(errorMessage, { timeOut: 3000 });
      }
    };

    const deleteCostPlan = async () => {
      try {
        await deleteCostPlanApi(selectedRowIds);
        ToastNotification.success("Cost deleted successfully!");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const fieldHistory = useMemo<IField<CostPlanType & CostPlanHistory>[]>(() => [
      {
        key: "vendorConnectionProfileId",
        header: "Provider",
        transform: async (t) => {
          if (!t.vendorConnectionProfileId) return "-";
          try {
            const provider = await queryClient.fetchQuery({
              queryKey: ["provider", t.vendorConnectionProfileId],
              queryFn: () => fetchProvider(t.vendorConnectionProfileId),
            });
            return provider?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!operator?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", operator.countryId],
              queryFn: () => fetchCountry(operator.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "CC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.cc ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "MCC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.mcc?.join(",") ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "MNC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return <TruncatedText text={operator?.mnc?.join(",")} />;
          } catch {
            return "-";
          }
        },
      },
      { key: "cost", header: "Cost" },
      { key: "newCost", header: "New Cost" },
      { key: "effectiveDate", header: "Effective Date" },
      { key: "historyTimestamp", header: "Date Modified" },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ], [queryClient]);

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        mode={dialogMode}
        title={
          dialogMode === "edit"
            ? "Edit Cost"
            : dialogMode === "history"
              ? "History"
              : dialogMode === "add"
                ? "Add New Cost "
                : "Delete Cost"
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={() => fetchCostPlanHistory(String(selectedId))}
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>
                        <p>Are you sure you want to delete this route?</p>
                      </Box>
                    ),
                  },
                ]
              : [
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Provider"
                        highlightLabelStar={true}
                        value={dialogData.vendorConnectionProfileId || ""}
                        onChange={async (
                          value: string | number | (string | number)[],
                        ) => {
                          const selectedProviderId =
                            value === undefined || value === null || value === ""
                              ? ""
                              : String(value);
                          setDialogData((prev) => ({
                            ...prev,
                            vendorConnectionProfileId: selectedProviderId,
                          }));
                          validateField(
                            "vendorConnectionProfileId",
                            selectedProviderId,
                          );

                          if (!selectedProviderId) {
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: "",
                            }));
                            return;
                          }

                          try {
                            const provider =
                              await fetchProvider(selectedProviderId);
                            const profile = await fetchProfile(
                              provider.profileId,
                            );
                            const currency = await fetchCurrency(
                              profile.currencyId,
                            );
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: currency.name,
                            }));
                          } catch (error: any) {
                            console.error(
                              "Error fetching currency for selected provider:",
                              error,
                            );
                            ToastNotification.error(error.message);
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: "",
                            }));
                          }
                        }}
                        options={providersOptions}
                        error={!!errors.vendorConnectionProfileId}
                        helperText={
                          errors.vendorConnectionProfileId
                            ? "Provider is required"
                            : ""
                        }
                        placeholder="Select Provider"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Country"
                        highlightLabelStar={true}
                        value={dialogData.countryId || ""}
                        onChange={async (
                          value: string | number | (string | number)[],
                        ) => {
                          const selectedCountryId = Number(value);
                          if (isNaN(selectedCountryId)) {
                            console.error("Invalid country ID");
                            return;
                          }

                          setDialogData((prev) => ({
                            ...prev,
                            countryId: Number(value),
                            operatorId: undefined,
                          }));

                          await loadOperatorsByCountry(selectedCountryId);

                          try {
                            handleCountryChange(value);
                          } catch (error: any) {
                            console.error(
                              "Error in handleCountryChange:",
                              error,
                            );
                          }
                        }}
                        options={countriesOptions}
                        error={!!errors.country}
                        helperText={errors.country ? "Country is required" : ""}
                        placeholder="Select Country"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },

                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Operator"
                        multiple={true}
                        selectAll={true}
                        hideChips={true}
                        value={
                          Array.isArray(dialogData.operatorId)
                            ? dialogData.operatorId.map(String)
                            : []
                        }
                        options={operatorsByCountry}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          const valueArray: number[] = Array.isArray(value)
                            ? value.map((v) => Number(v))
                            : value
                              ? [Number(value)]
                              : [];

                          console.log("Updated operatorId:", valueArray);

                          setDialogData((prev) => ({
                            ...prev,
                            operatorId: valueArray,
                          }));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.operatorId}
                        placeholder="Select Operator"
                        highlightLabelStar={true}
                        helperText={
                          errors.operatorId ? "Operator is required" : ""
                        }
                        disabled={isOperatorDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "text",
                    typeinput: "number",
                    label: "Cost",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.cost !== undefined ? dialogData.cost : "",
                    onChange: (value) => {
                      const numericValue =
                        value === "" ? undefined : Number(value);
                      setDialogData((prev) => ({
                        ...prev,
                        cost: numericValue,
                      }));
                    },

                    placeholder: "Enter Cost",
                    error: !!errors.cost,
                    helperText: errors.cost ? "Cost is required" : "",
                  },
                  {
                    type: "text",
                    label: "Currency",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: false,
                    value: dialogData.currencyName ?? "",
                    disabled: true,
                    onChange: (value) => {},
                    placeholder: "Existing Currency",
                  },

                  {
                    type: "custom",
                    highlightLabelStar: true,
                    component: (
                      <CustomDateTimePicker
                        label="Effective Date"
                        value={
                          dialogData.effectiveDate
                            ? new Date(dialogData.effectiveDate)
                            : null
                        }
                        onChange={(date: Date | null) => {
                          setDialogData((prev) => ({
                            ...prev,
                            effectiveDate: date || undefined,
                          }));
                        }}
                        error={!!errors.effectiveDate}
                        helperText={
                          errors.effectiveDate
                            ? "Effective Date is required"
                            : ""
                        }
                      />
                    ),
                  },
                  {
                    type: "text",
                    label: "Comment",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: false,
                    value: dialogData.comment ?? "",
                    onChange: (value) => {
                      setDialogData((prev) => ({
                        ...prev,
                        comment: String(value),
                      }));
                    },
                    placeholder: "Enter comment",
                    error: !!errors.comment,
                    helperText: errors.comment ? "comment is required" : "",
                  },
                ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                dialogMode !== "delete"
                  ? {
                      label: "Save",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        await addCostPlan(dialogData);
                      },
                      disabled:
                        isSaveDisabled ||
                        (dialogMode === "edit"
                          ? !hasJwtPermission("PUT_ROUTING_COST")
                          : !hasJwtPermission("POST_ROUTING_COST")),
                    }
                  : {
                      label: "Delete",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      textColor: COLORS.iconColor,
                      borderRadius: "12px",
                      onClick: deleteCostPlan,
                      disabled: !hasJwtPermission("DELETE_ROUTING_COST"),
                    },
              ]
        }
      />
    );
  },
);

export default CostPlanPage;
